"use client"

import React, { useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Facebook, Instagram, Linkedin, LinkedinIcon, Youtube } from "lucide-react"
import { useI18n } from "@/context/i18n"
import { pickLocalized } from "@/lib/i18nUtils"

export default function Footer() {
  const { t, lang } = useI18n()
  const pathname = usePathname() || ''
  const [ratesDate, setRatesDate] = useState<string | null>(null)
  const shouldHide = pathname.includes('/admin') || pathname.startsWith('/landings')

  useEffect(() => {
    if (shouldHide) return
    let mounted = true
    ;(async () => {
      try {
        const res = await fetch('/api/rates-date', { cache: 'no-store' })
        if (!res.ok) throw new Error('fetch failed')
        const payload = await res.json()
        const meta = payload?.rates_meta ?? null
        let dateStr = meta?.rates_date || meta?.updated_at || null
        // Normalize YYYY-MM-DD (no timezone) to an ISO date at UTC to avoid client TZ shifts
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          const [y, m, d] = dateStr.split('-').map(s => parseInt(s, 10))
          if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
            // store as ISO date string in UTC (no time) to be parsed consistently on client
            const utc = new Date(Date.UTC(y, m - 1, d))
            dateStr = utc.toISOString()
          }
        }
        if (mounted && dateStr) setRatesDate(dateStr)
      } catch (err) {
        // ignore, keep null to fall back to client date
      }
    })()
    return () => { mounted = false }
  }, [shouldHide])

  if (shouldHide) {
    return null
  }

  return (
  <footer className="bg-black text-white mt-16 z-10">
      {/* Skyline superior, fondo blanco y ciudad negra */}
      <div className="w-full select-none bg-white">
        <div className="w-full h-36 sm:h-48 md:h-56 lg:h-64 overflow-hidden relative">
          <Image src="https://drive.google.com/uc?export=view&id=1eGCMEVNQRoNJ0aaXp6BtoEYQa8F8tD_c" alt="" fill className="object-cover object-bottom block filter contrast-150" />
        </div>
      </div>

      <div className="container mx-auto px-4 py-10 md:py-12">
        <div className="grid gap-10 sm:grid-cols-1 md:grid-cols-4 lg:grid-cols-4">
          {/* Column 1: Brand (title, slogan, social) */}
          <div>
            <Link href="/" className="inline-block" aria-label="Inicio LET">
              <Image
                src="https://drive.google.com/uc?export=view&id=1cvLXw-a7IIgGYoHJKsIbVY17VdO4hvgy"
                alt="Logotipo de LET"
                width={240}
                height={140}
                className="filter contrast-150"
              />
            </Link>
            <p className="mt-4 text-sm text-white/70 max-w-sm leading-relaxed">
              Acompañamos tu proyecto internacional con asesoría, experiencia y aliados confiables.
            </p>
            <div className="mt-4 flex gap-3">
              <a
                href="https://www.facebook.com/LinkEducationTravel"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white/80 hover:text-white hover:bg-primary focus:outline-none focus:ring-2 focus:ring-white/40"
                aria-label="Facebook"
                data-ga-action="social_click"
                data-ga-category="social"
                data-ga-label="Facebook"
              >
                <span className="sr-only">Facebook</span>
                <Facebook className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="https://www.instagram.com/linkeducationandtravel/?hl=es-la"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white/80 hover:text-white hover:bg-primary focus:outline-none focus:ring-2 focus:ring-white/40"
                aria-label="Instagram"
                data-ga-action="social_click"
                data-ga-category="social"
                data-ga-label="Instagram"
              >
                <span className="sr-only">Instagram</span>
                <Instagram className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="https://www.youtube.com/@linkeducationandtravel"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white/80 hover:text-white hover:bg-primary focus:outline-none focus:ring-2 focus:ring-white/40"
                aria-label="YouTube"
                data-ga-action="social_click"
                data-ga-category="social"
                data-ga-label="YouTube"
              >
                <span className="sr-only">YouTube</span>
                <Youtube className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="https://ca.linkedin.com/company/linkeducationca"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white/80 hover:text-white hover:bg-primary focus:outline-none focus:ring-2 focus:ring-white/40"
                aria-label="LinkedIn"
                data-ga-action="social_click"
                data-ga-category="social"
                data-ga-label="LinkedIn"
              >
                <span className="sr-only">LinkedIn</span>
                <Linkedin className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Column 2: Education services */}
          <div className="text-sm">
            <h3 className="font-semibold text-lg mb-3">{t('nav_lets_study')}</h3>
            <ul className="space-y-2 text-white/80">
              <li><Link href="/servicios/idiomas" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_study_children_languages')}</Link></li>
              <li><Link href="/servicios/estudia-y-trabaja" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_study_children_work_and_study')}</Link></li>
              <li><Link href="/servicios/highschool" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_study_children_highschool')}</Link></li>
              <li><Link href="/servicios/aupair" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_study_children_aupair')}</Link></li>
              <li><Link href="/servicios/pathway" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_study_children_pathway')}</Link></li>
              <li><Link href="/servicios/educacion-superior" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_study_children_higher_education')}</Link></li>
              <li><Link href="/servicios/ingles-online" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_study_children_online_english')}</Link></li>
              <li><Link href="/servicios/expo-estudia-viajando-2027" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_study_children_expo')}</Link></li>
            </ul>
          </div>

          {/* Column 3: Travel services */}
          <div className="text-sm">
            <h3 className="font-semibold text-lg mb-3">{t('nav_lets_travel')}</h3>
            <ul className="space-y-2 text-white/80">
              <li><Link href="/servicios/viajes-internacionales" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_travel_children_international_travel')}</Link></li>
              <li><Link href="/servicios/viajes-nacionales" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_travel_children_domestic_travel')}</Link></li>
              <li><Link href="/servicios/despedida-de-solteros" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_travel_children_bachelor_parties')}</Link></li>
              <li><Link href="/servicios/lunas-de-miel" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_travel_children_honeymoons')}</Link></li>
              <li><Link href="/servicios/cruceros" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_travel_children_cruises')}</Link></li>
              <li><Link href="/servicios/quinceaneras" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_travel_children_quinceaneras')}</Link></li>
            </ul>
          </div>

          {/* Column 4: Inmigrate services */}
          <div className="text-sm">
            <h3 className="font-semibold text-lg mb-3">{t('nav_lets_inmigrate')}</h3>
            <ul className="space-y-2 text-white/80">
              <li><Link href="/servicios/migracion-canada" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_inmigrate_children_canada')}</Link></li>
              <li><Link href="/categoria/participaciones/australia" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_inmigrate_australia')}</Link></li>
              <li><Link href="/servicios/visa-americana" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_travel_children_visa_americana')}</Link></li>
              <li><Link href="/servicios/visa-canadiense" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5">{t('lets_travel_children_visa_canadiense')}</Link></li>
            </ul>
          </div>
        </div>

        {/* Second row: additional menu items aligned under each column (Promociones, Recursos, Nosotros, Tipo de cambio/Idioma) */}
        <div className="mt-8 grid gap-10 sm:grid-cols-1 md:grid-cols-4 lg:grid-cols-4">
          {/* Promociones */}
          <div className="text-sm">
            <h3 className="font-semibold text-lg mb-3">{t('nav_promociones')}</h3>
            <ul className="space-y-2 text-white/80 mb-6">
              <li><Link href="/promociones" className="hover:text-white">{pickLocalized(lang, 'Revisa nuestras promociones', 'Check our promotions', 'Découvrez nos promotions')}</Link></li>
            </ul>
          </div>

          {/* Recursos */}
          <div className="text-sm">
            <h3 className="font-semibold text-lg mb-3">{t('nav_recursos')}</h3>
            <ul className="space-y-2 text-white/80">
            <li><Link href="https://acceso-padres.grupo-let.com/login" className="hover:text-white">{pickLocalized(lang, 'Acceso a Padres', 'Parent Access', 'Accès aux parents')}</Link></li>
              <li><Link href="/servicios/grupos-educativos/tutorial-acceso-padres" className="hover:text-white">{pickLocalized(lang, 'Tutorial Acceso a Padres', 'Parent Access Tutorial', 'Tutoriel Accès parents')}</Link></li>
              <li><Link href="/blog" className="hover:text-white">{pickLocalized(lang, 'Noticias, Blog y Webinars', 'News, Blog and Webinars', 'Nouvelles, Blog et Webinaires')}</Link></li>
              <li><Link href="/divisas" className="hover:text-white">{t('nav_divisas')}</Link></li>
              <li><Link href="/podcast" className="hover:text-white">Podcast</Link></li>
              <li><Link href="/bolsa_trabajo" className="hover:text-white">{pickLocalized(lang, 'Bolsa de Trabajo', 'Job board', 'Bourse d\'emploi')}</Link></li>
              <li><Link href="/mapa-destinos" className="hover:text-white">{pickLocalized(lang, 'Mapa de Destinos', 'Destinations Map', 'Carte des destinations')}</Link></li>
              <li><Link href="/categoria/participaciones/colegios" className="hover:text-white">{pickLocalized(lang, 'Colegios asociados', 'Partner Schools', 'Ecoles partenaires')}</Link></li>
              <li><Link href="/categoria/participaciones" className="hover:text-white">{t('nav_participations')}</Link></li>
            </ul>
          </div>

          {/* Nosotros & Contacto */}
          <div className="text-sm">
            <h3 className="font-semibold text-lg mb-3">{t('footer_nosotros')}</h3>
            <ul className="space-y-2 text-white/80">
              <li><Link href="/nosotros" className="hover:text-white">{t('footer_sobre_nosotros')}</Link></li>
              <li><Link href="/#office-locator" className="hover:text-white">{t('footer_sucursales')}</Link></li>
              <li><Link href="/pagos" className="hover:text-white">{pickLocalized(lang, 'Pago en Línea', 'Online Payment', 'Paiement en ligne')}</Link></li>
            </ul>

            <div className="mt-8">
              <h3 className="font-semibold text-lg mb-3">{pickLocalized(lang, 'Contacto', 'Contact', 'Contact')}</h3>
              <ul className="space-y-2 text-white/80 leading-relaxed text-xs">
                <li><a href="mailto:hola@grupo-let.com" className="hover:text-white">hola@grupo-let.com</a></li>
                <li><a href="tel:+527222352370" className="hover:text-white">+52 722 235 2370</a></li>
                <li className="mt-2 text-white/60">
                  Calle Andrés Quintana Roo #529,<br/>
                  Col. Francisco Murguía, El Ranchito,<br/>
                  C.P 50130, Toluca, México
                </li>
              </ul>
            </div>
          </div>

          {/* Tipo de cambio / Idioma */}
          <div className="text-sm">
            <h3 className="mb-6">
              {/* Reserva de espacio para evitar CLS cuando llega la fecha (aprox altura de una línea) */}
              <div className="min-h-[1.75rem]">
                {ratesDate ? (
                  <p className="font-semibold text-lg mb-2">
                    {pickLocalized(lang, 'Tipo de cambio al', 'Exchange rate as of', 'Taux de change au')}{' '}
                    {(() => {
                      try {
                        const locale = lang === 'EN' ? 'en-US' : 'es-MX'
                        return new Intl.DateTimeFormat(locale, { timeZone: 'UTC' }).format(new Date(ratesDate))
                      } catch {
                        return ''
                      }
                    })()}
                  </p>
                ) : null}
              </div>
              <Link href="/divisas" className="space-y-2 text-white hover:text-white/90 text-sm underline hover:no-underline transition-all">{pickLocalized(lang, 'Revisa tipo de cambio', 'Check exchange rates', 'Vérifiez le taux de change')}</Link>
            </h3>
            <div className="mt-12 transform scale-125 origin-left">
              <span id='iasBadge' data-account-id='2623'></span>
              <script async defer crossOrigin="anonymous" src="https://www-cdn.icef.com/scripts/iasbadgeid.js"></script>
            </div>
          </div>
        </div>

        <div className="mt-10 border-t border-white/10 pt-6 text-xs text-white/60 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} LET. {t('footer_copy')}</p>
          <div className="flex items-center gap-4">
            <Link href="/aviso-de-privacidad" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5" data-ga-action="footer_click" data-ga-category="navigation" data-ga-label="aviso">{t('footer_avisopriv')}</Link>
            <Link href="/terminos-y-condiciones" className="hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded px-1 py-0.5" data-ga-action="footer_click" data-ga-category="navigation" data-ga-label="terminos y condiciones">{t('footer_terminos')}</Link>
            <button
              type="button"
              onClick={() => {
                if (typeof window !== "undefined") {
                  window.scrollTo({ top: 0, behavior: "smooth" })
                }
              }}
              className="ml-2 rounded px-2 py-1 text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
               aria-label={t('footer_arriba')}
               data-ga-action="back_to_top"
               data-ga-category="engagement"
               data-ga-label="footer"
            >
              {t('footer_arriba')}
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}
